import { useState, useRef, useCallback, useEffect } from 'react'
import { AudioResult } from './api/mutations/ft-voice-note/useFtVoiceNote'

export type { AudioResult }

interface UseVoiceRecorderReturn {
  isRecording: boolean
  /** Resolves false when recording could not start (microphone refused or unavailable). */
  startRecording: () => Promise<boolean>
  stopRecording: () => Promise<AudioResult>
  duration: number
}

export const useVoiceRecorder = (): UseVoiceRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false)
  const [duration, setDuration] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const resolveStopRef = useRef<((result: AudioResult) => void) | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
    }
  }, [])

  const startRecording = useCallback(async () => {
    let stream: MediaStream | undefined
    try {
      // Fails when the microphone is refused or missing, on a page that is not
      // secure (no mediaDevices), or in a browser that cannot record audio/webm.
      const granted = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream = granted
      const mediaRecorder = new MediaRecorder(granted, { mimeType: 'audio/webm' })
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const uri = URL.createObjectURL(blob)
        granted.getTracks().forEach((track) => track.stop())
        if (resolveStopRef.current) {
          resolveStopRef.current({ uri, type: 'audio/webm', name: 'voice-note.webm', blob })
          resolveStopRef.current = null
        }
      }

      mediaRecorder.start()
    } catch {
      stream?.getTracks().forEach((track) => track.stop())
      mediaRecorderRef.current = null
      return false
    }
    setIsRecording(true)
    setDuration(0)
    timerRef.current = setInterval(() => {
      setDuration((d) => d + 1)
    }, 1000)
    return true
  }, [])

  const stopRecording = useCallback((): Promise<AudioResult> => {
    return new Promise((resolve) => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      setIsRecording(false)
      resolveStopRef.current = resolve
      mediaRecorderRef.current?.stop()
    })
  }, [])

  return { isRecording, startRecording, stopRecording, duration }
}
